/**
 * The camera calibrations: list, inspect and download their input files.
 *
 * A calibration is the mapping from camera RGB to CIE XYZ for one camera and
 * lighting setup; every colour an exploration measures passes through it. The
 * download serves the archived inputs (spectra and RAW captures) the
 * calibration was fitted from, so a result can be re-derived later.
 *
 * The page shares its look with the New exploration page: one card per
 * calibration (components/ResourceCard.js) under a group label, with the
 * summary panel and the actions beside them (components/ResourcePanel.js).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import axios from "axios";
import ResourcePageLayout from "../../components/ResourcePageLayout";
import ResourcePanel, { PanelButton } from "../../components/ResourcePanel";
import ResourceCard, { DeleteResourceButton, usagePill } from "../../components/ResourceCard";
import FactTiles from "../../components/FactTiles";
import { LIST_SCOPE_ALL, withListScope } from "../../components/ListScopeToggle";
import { MONO_FONT, dataTableSx, overlineSx } from "../../components/cardChrome";
import { formatDateTime } from "../../helper/formatDate";
import { formatDeltaE } from "../../helper/formatDeltaE";
import { downloadBlob, getFilenameFromContentDisposition } from "../../helper/markingPreviewWindow";

/**
 * Colour of a ΔE00 value: green under 2, amber under 4, red above. Rough
 * visual guide only — the numbers themselves are what matters.
 */
const deChipColor = (value) => {
  if (value == null) return "default";
  if (value < 2) return "success";
  if (value < 4) return "warning";
  return "error";
};

/** The archived calibration inputs (detail `inputFiles` keys), named as in the wizard. */
const INPUT_FILES = [
  { key: "spectra", label: "Ground-truth spectra" },
  { key: "color_image", label: "Calibration patches image" },
  { key: "norm_image", label: "Normalization image" },
];

// Blob-typed requests deliver error bodies as a Blob — decode the
// backend's {error} detail if there is one.
const extractErrorDetail = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).error || null;
    } catch (parseError) {
      return null;
    }
  }
  return data?.error || null;
};

function SectionLabel({ children }) {
  return (
    <Typography variant="overline" sx={{ ...overlineSx, mb: 0.75 }}>
      {children}
    </Typography>
  );
}

/**
 * One calibration: the header shows the camera, the illumination, the
 * leave-one-out ΔE00 and the usage; the details (loaded on first expand)
 * show the evaluation, the archived inputs, the worst patches and the
 * transformation matrix.
 */
function CalibrationCard({ row, onDeleteRequest }) {
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const evaluation = row.evaluation;
  const usedBy = row.explorationsCount ?? 0;

  const handleToggle = async (open) => {
    if (!open || detail || detailBusy) return;
    setDetailBusy(true);
    setDetailError(null);
    try {
      const response = await axios.get(
        `api/calibration/camera/evaluations?id=${encodeURIComponent(row.id)}`
      );
      setDetail(response.data?.calibration || null);
    } catch (error) {
      console.error("Loading calibration detail failed:", error);
      setDetailError("Could not load the calibration details.");
    } finally {
      setDetailBusy(false);
    }
  };

  const inputFiles = detail?.inputFiles || {};
  const archivedInputs = INPUT_FILES.filter(({ key }) => inputFiles[key]);

  const handleDownload = async (key) => {
    setDownloading(key);
    setDownloadError(null);
    try {
      // The RAWs are ~50-60 MB each.
      const response = await axios.get("api/calibration/camera/input", {
        params: { id: row.id, file: key },
        responseType: "blob",
        timeout: 600000,
      });
      const filename =
        getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
        `${row.name}_${inputFiles[key]}`;
      downloadBlob(response.data, filename);
    } catch (error) {
      console.error("Downloading calibration input failed:", error);
      setDownloadError((await extractErrorDetail(error)) || "Could not download the file.");
    } finally {
      setDownloading(null);
    }
  };

  const patches = detail?.details?.patches || [];
  const worstPatches = [...patches]
    .sort((a, b) => (b.loo_de00 ?? b.de00 ?? 0) - (a.loo_de00 ?? a.de00 ?? 0))
    .slice(0, 10);
  const matrix = Array.isArray(detail?.transformationMatrix)
    ? detail.transformationMatrix.filter((matrixRow) => Array.isArray(matrixRow))
    : [];
  // R/G/B only label a plain 3x3. A polynomial fit (Nikon_polycal is 3x11)
  // carries term columns instead, so number those rather than mislabel the
  // first three and run the rest off the end of the header.
  const matrixCols = matrix.reduce((widest, matrixRow) => Math.max(widest, matrixRow.length), 0);
  const matrixHeaders =
    matrixCols === 3
      ? ["R", "G", "B"]
      : Array.from({ length: matrixCols }, (_, index) => `c${index + 1}`);
  const warnings = evaluation?.warnings || [];

  const pills = [];
  if (row.cameraDevice) pills.push({ key: "camera", label: "Camera", value: row.cameraDevice });
  if (row.illuminationDevice) {
    pills.push({ key: "illumination", label: "Illumination", value: row.illuminationDevice });
  }
  // The ΔE00 figures stay in the details (expand the card); the header
  // names the camera, the illumination and the usage only.
  pills.push(usagePill(usedBy));

  return (
    <ResourceCard
      cardKey={row.id}
      icon={<CameraAltRoundedIcon sx={{ fontSize: 20 }} />}
      title={row.name}
      caption={[row.createdBy, row.createdAt ? formatDateTime(row.createdAt) : null]}
      pills={pills}
      actions={
        <DeleteResourceButton usedBy={usedBy} noun="calibration" onClick={() => onDeleteRequest(row)} />
      }
      onToggle={handleToggle}
    >
      <Stack spacing={2.5}>
        {evaluation ? (
          <>
            <FactTiles
              facts={[
                {
                  label: "Patches",
                  value: `${evaluation.numPatches} (${evaluation.grid?.[0]}×${evaluation.grid?.[1]} grid)`,
                },
                { label: "Function order", value: String(evaluation.functionOrder) },
              ]}
            />
            <Box>
              <SectionLabel>ΔE00 against the reference patches</SectionLabel>
              <Table size="small" sx={{ maxWidth: 560, ...dataTableSx }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell align="right">Mean</TableCell>
                    <TableCell align="right">Median</TableCell>
                    <TableCell align="right">p95</TableCell>
                    <TableCell align="right">Max</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ["In-sample", evaluation.inSample],
                    ["Leave-one-out", evaluation.leaveOneOut],
                  ].map(([label, stats]) => (
                    <TableRow key={label}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          color={deChipColor(stats?.mean)}
                          label={formatDeltaE(stats?.mean)}
                          variant="outlined"
                          sx={{ fontFamily: MONO_FONT, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatDeltaE(stats?.median)}</TableCell>
                      <TableCell align="right">{formatDeltaE(stats?.p95)}</TableCell>
                      <TableCell align="right">{formatDeltaE(stats?.max)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            This calibration was created without the D55 evaluation procedure (seeded default
            or legacy import), so no quality data is stored for it — the transformation matrix
            below is all there is.
          </Typography>
        )}
        {warnings.map((warning, index) => (
          <Alert key={index} severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            {warning}
          </Alert>
        ))}
        {detailBusy ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading calibration details…
            </Typography>
          </Stack>
        ) : detailError ? (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
            {detailError}
          </Alert>
        ) : (
          <>
            {archivedInputs.length > 0 ? (
              <Box>
                <SectionLabel>Input files</SectionLabel>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {archivedInputs.map(({ key, label }) => (
                    <Tooltip key={key} title={inputFiles[key]}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            downloading === key ? <CircularProgress size={14} /> : <DownloadIcon />
                          }
                          disabled={downloading !== null}
                          onClick={() => handleDownload(key)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: "rgba(15, 23, 42, 0.16)",
                            color: "text.primary",
                            "&:hover": {
                              borderColor: "rgba(15, 23, 42, 0.3)",
                              bgcolor: "rgba(15, 23, 42, 0.03)",
                            },
                          }}
                        >
                          {label}
                        </Button>
                      </span>
                    </Tooltip>
                  ))}
                </Stack>
                {downloadError ? (
                  <Alert severity="error" variant="outlined" sx={{ mt: 1, borderRadius: 2 }}>
                    {downloadError}
                  </Alert>
                ) : null}
              </Box>
            ) : null}
            {worstPatches.length > 0 ? (
              <Box>
                <SectionLabel>Worst patches (by leave-one-out ΔE00)</SectionLabel>
                <Table size="small" sx={{ maxWidth: 720, ...dataTableSx }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Patch (row, col)</TableCell>
                      <TableCell align="right">ref L*a*b*</TableCell>
                      <TableCell align="right">measured L*a*b*</TableCell>
                      <TableCell align="right">ΔE00</TableCell>
                      <TableCell align="right">LOO ΔE00</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {worstPatches.map((patch) => (
                      <TableRow key={patch.patch}>
                        <TableCell>
                          #{patch.patch} ({patch.row}, {patch.col})
                        </TableCell>
                        <TableCell align="right">
                          {(patch.ref_lab || []).map((v) => v.toFixed(1)).join(", ")}
                        </TableCell>
                        <TableCell align="right">
                          {(patch.meas_lab || []).map((v) => v.toFixed(1)).join(", ")}
                        </TableCell>
                        <TableCell align="right">{formatDeltaE(patch.de00)}</TableCell>
                        <TableCell align="right">{formatDeltaE(patch.loo_de00)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
            {matrix.length > 0 ? (
              <Box>
                <SectionLabel>
                  Transformation matrix (camera RGB → CIEXYZ)
                  {matrixCols === 3 ? null : ` — ${matrix.length}×${matrixCols} polynomial`}
                </SectionLabel>
                <Table size="small" sx={{ maxWidth: 420, ...dataTableSx }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {matrixHeaders.map((header) => (
                        <TableCell key={header} align="right">
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matrix.map((matrixRow, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell>{["X", "Y", "Z"][rowIndex] ?? rowIndex}</TableCell>
                        {matrixRow.map((value, colIndex) => (
                          <TableCell key={colIndex} align="right">
                            {Number(value).toPrecision(4)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
          </>
        )}
      </Stack>
    </ResourceCard>
  );
}

/**
 * Overview of all camera calibrations with their stored D55 evaluations, so
 * calibrations can be compared and judged before being used in an exploration.
 */
export default function CameraCalibrationsOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const navigate = useNavigate();

  const loadCalibrations = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get(
        withListScope("api/calibration/camera/evaluations", LIST_SCOPE_ALL)
      );
      setRows(response.data?.calibrations || []);
    } catch (error) {
      console.error("Loading calibrations failed:", error);
      setLoadError("Could not load the camera calibrations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalibrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await axios.delete(
        `api/color/explorer/color/calibration/${encodeURIComponent(deleteTarget.id)}/`
      );
      setDeleteTarget(null);
      await loadCalibrations();
    } catch (error) {
      console.error("Deleting calibration failed:", error);
      setDeleteError(
        error?.response?.data?.error || "Could not delete the calibration."
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const inUse = rows.filter((row) => (row.explorationsCount ?? 0) > 0).length;
  const panel = (
    <ResourcePanel
      overline="All calibrations"
      headline={
        loading ? "Loading" : `${rows.length} ${rows.length === 1 ? "calibration" : "calibrations"}`
      }
      subline={loading ? "" : `${inUse} in use · ${rows.length - inUse} unused`}
      about="A calibration maps camera RGB to CIE XYZ for one camera and lighting setup; every colour an exploration measures passes through it. Calibrations are never edited, and one in use cannot be deleted. Expand a calibration for its ΔE00 figures: green below 2, amber below 4, red above."
      actions={
        <>
          <PanelButton
            primary
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate("/colorexplorationcalibrationcamera")}
          >
            New calibration
          </PanelButton>
          <PanelButton startIcon={<RefreshIcon />} onClick={loadCalibrations} disabled={loading}>
            Refresh
          </PanelButton>
        </>
      }
    />
  );

  return (
    <>
      <ResourcePageLayout
        title="Calibrations"
        loading={loading}
        loadingLabel="Loading calibrations…"
        error={loadError}
        count={rows.length}
        emptyText={"No camera calibrations yet. Create one with “New calibration”."}
        panel={panel}
      >
        {rows.map((row) => (
          <CalibrationCard
            key={row.id}
            row={row}
            onDeleteRequest={(target) => {
              setDeleteError(null);
              setDeleteTarget(target);
            }}
          />
        ))}
      </ResourcePageLayout>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete camera calibration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the calibration “{deleteTarget?.name}”, its per-patch
            evaluation data and the archived input files. This cannot be undone.
          </DialogContentText>
          {deleteError ? (
            <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleteBusy}>
            {deleteBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
